/**
 * Card art with a fallback.
 *
 * Art paths come from the card data (`art.path`, relative to /public) and are
 * generated by `npm run art`. If a file is missing, or the set grows before the
 * art script is re-run, the frame falls back to the faction glyph over the
 * material gradient rather than showing a broken image.
 */

import { useEffect, useState } from 'react';
import { FactionGlyph } from '@/ui/icons/FactionGlyph';
import type { CardView } from './cardView';
import styles from './CardFrame.module.css';

export function CardArt({ card }: { card: CardView }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [card.artPath]);

  if (!card.artPath || failed) {
    return (
      <span className={styles.artFallback} aria-hidden="true">
        <FactionGlyph faction={card.faction} size={34} color="var(--mat)" />
      </span>
    );
  }

  return (
    <img
      className={styles.artImg}
      src={artUrl(card.artPath)}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
}

/** Art paths are stored relative to /public, so they need a leading slash. */
export function artUrl(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}
